import { useEffect, useRef } from 'react';
import { apiClient } from '@/lib/api-client';
import { collectDeviceInfo } from '@/utils/device-info';
import { useCurrentUser } from './use-current-user';

/**
 * Hook to automatically track device information
 * Registers device on mount if user is authenticated
 * Uses a ref to prevent multiple registrations in the same session
 */
export function useDeviceTracking(accessType: 'login' | 'api_call' | 'page_view' = 'page_view', endpoint?: string) {
  const { user } = useCurrentUser();
  const hasRegisteredRef = useRef(false);
  const registrationAttemptedRef = useRef(false);

  useEffect(() => {
    // Only register if user is authenticated and we haven't registered yet in this session
    if (!user || hasRegisteredRef.current || registrationAttemptedRef.current) {
      return;
    }

    // Mark that we're attempting registration to prevent duplicate calls
    registrationAttemptedRef.current = true;

    // Collect device info and register asynchronously (non-blocking)
    const registerDevice = async () => {
      try {
        const deviceInfo = collectDeviceInfo();
        
        await apiClient.registerDevice({
          ...deviceInfo,
          accessType,
          endpoint: endpoint || window.location.pathname,
        });

        // Mark as registered only on success
        hasRegisteredRef.current = true;
      } catch (error) {
        // Silently fail - device tracking should not break the app
        console.error('Failed to register device:', error);
        // Reset attempt flag so we can retry later if needed
        registrationAttemptedRef.current = false;
      }
    };

    // Register device (fire and forget)
    registerDevice();
  }, [user, accessType, endpoint]);

  return {
    isRegistered: hasRegisteredRef.current,
  };
}

/**
 * Manual device registration function
 * Can be called explicitly (e.g., after login)
 */
export async function registerDeviceManually(
  accessType: 'login' | 'api_call' | 'page_view' = 'page_view',
  endpoint?: string
): Promise<void> {
  try {
    const deviceInfo = collectDeviceInfo();
    
    await apiClient.registerDevice({
      ...deviceInfo,
      accessType,
      endpoint: endpoint || (typeof window !== 'undefined' ? window.location.pathname : undefined),
    });
  } catch (error) {
    console.error('Failed to register device manually:', error);
    throw error;
  }
}

